import {
  MAX_OUTPUT_FPS,
  MIN_TRIM_SECONDS,
  PREVIEW_FRAME_CAP,
  clamp,
  el,
  formatBytes,
  formatSpeed,
  formatTime,
  getOutputDimensions,
  getSettings,
  state,
} from "./state.js"

const BLUR_PAYLOAD_MIX = 1e-7
const MIX_EPSILON = 1e-6

function lowerBound(values, target) {
  let low = 0
  let high = values.length
  while (low < high) {
    const middle = (low + high) >> 1
    if (values[middle] < target) low = middle + 1
    else high = middle
  }
  return low
}

function upperBound(values, target) {
  let low = 0
  let high = values.length
  while (low < high) {
    const middle = (low + high) >> 1
    if (values[middle] <= target) low = middle + 1
    else high = middle
  }
  return low
}

export function sourceIndexAtTime(time) {
  const timestamps = state.sourceFrameTimestamps
  if (!timestamps.length) return -1
  return clamp(upperBound(timestamps, time) - 1, 0, timestamps.length - 1)
}

export function getTrimSourceIndices() {
  const timestamps = state.sourceFrameTimestamps
  if (!timestamps.length || state.trimEnd <= state.trimStart) return []
  const first = Math.max(0, upperBound(timestamps, state.trimStart) - 1)
  const end = lowerBound(timestamps, state.trimEnd)
  const result = []
  for (let index = first; index < Math.max(first + 1, end); index += 1) {
    if (index >= timestamps.length) break
    const frameStart = timestamps[index]
    const frameEnd = frameStart + (state.sourceFrameDurations[index] || 0)
    if (frameStart < state.trimEnd && frameEnd > state.trimStart - 1e-6) result.push(index)
  }
  return result.length ? result : [sourceIndexAtTime(state.trimStart)]
}

function sampleEvenly(values, limit) {
  if (values.length <= limit) return values.slice()
  if (limit <= 1) return [values[0]]
  const result = []
  for (let index = 0; index < limit; index += 1) {
    const sourceIndex = Math.round(index * (values.length - 1) / (limit - 1))
    const value = values[sourceIndex]
    if (result.at(-1) !== value) result.push(value)
  }
  return result
}

function dominantSource(spec) {
  if (!spec) return -1
  return Number(spec.mix) >= 0.5 ? spec.to : spec.from
}

function directionProgress(progress) {
  if (state.direction === "reverse") return 1 - progress
  if (state.direction === "rebound") return progress < 0.5 ? progress * 2 : 2 - progress * 2
  return progress
}

function sourceSpecAtTime(time, morphAmount, motionBlur) {
  const timestamps = state.sourceFrameTimestamps
  const lastIndex = timestamps.length - 1
  if (lastIndex < 0) return { from: 0, to: 0, mix: 0, baseFrame: true }

  const previous = sourceIndexAtTime(time)
  const next = clamp(previous + 1, 0, lastIndex)
  const previousTime = timestamps[previous] ?? time
  const nextTime = timestamps[next] ?? previousTime
  const naturalMix = next !== previous && nextTime > previousTime
    ? clamp((time - previousTime) / (nextTime - previousTime), 0, 1)
    : 0
  const nearestMix = naturalMix >= 0.5 ? 1 : 0
  const morphStrength = clamp(Number(morphAmount) || 0, 0, 100) / 100

  if (!morphStrength || next === previous) {
    const selected = nearestMix ? next : previous
    if (!motionBlur || lastIndex < 1) return { from: selected, to: selected, mix: 0, baseFrame: true }
    const neighbor = selected < lastIndex ? selected + 1 : selected - 1
    return {
      from: selected,
      to: neighbor,
      mix: neighbor === selected ? 0 : BLUR_PAYLOAD_MIX,
      baseFrame: true,
    }
  }

  const mix = nearestMix + (naturalMix - nearestMix) * morphStrength
  const interpolated = mix > MIX_EPSILON && mix < 1 - MIX_EPSILON
  if (!interpolated) {
    const selected = mix >= 0.5 ? next : previous
    if (!motionBlur || lastIndex < 1) return { from: selected, to: selected, mix: 0, baseFrame: true }
    const neighbor = selected < lastIndex ? selected + 1 : selected - 1
    return {
      from: selected,
      to: neighbor,
      mix: neighbor === selected ? 0 : BLUR_PAYLOAD_MIX,
      baseFrame: true,
    }
  }

  return {
    from: previous,
    to: next,
    mix,
    morphFrame: true,
  }
}

export function morphSequence(sequence, morphAmount, motionBlur = 0) {
  if (!sequence.length) return []
  const strength = clamp(Number(morphAmount) || 0, 0, 100) / 100
  return sequence.map((current, index) => {
    const neighbor = index < sequence.length - 1 ? sequence[index + 1] : sequence[index - 1]
    if (!strength || neighbor == null || neighbor === current) {
      if (motionBlur && neighbor != null && neighbor !== current) {
        return { from: current, to: neighbor, mix: BLUR_PAYLOAD_MIX, baseFrame: true }
      }
      return { from: current, to: current, mix: 0, baseFrame: true }
    }
    const mix = index % 2 ? 0.5 * strength : 0
    return mix > MIX_EPSILON
      ? { from: current, to: neighbor, mix, morphFrame: true }
      : { from: current, to: current, mix: 0, baseFrame: true }
  })
}

export function getAppliedMorphFrames(settings) {
  return clamp(Number(settings.morphAmount) || 0, 0, 100)
}

export function getFramePlan(settings = getSettings()) {
  const trimDuration = Math.max(MIN_TRIM_SECONDS, state.trimEnd - state.trimStart)
  const speed = clamp(state.speed, 0.1, 8)
  const selectedFps = clamp(settings.fps, 1, MAX_OUTPUT_FPS)
  const sourceFps = clamp(state.sourceFps || selectedFps, 1, 240)
  const directionFactor = state.direction === "rebound" ? 2 : 1
  const nominalAnimationDuration = trimDuration * directionFactor / speed
  const requestedFrameCount = Math.max(2, Math.round(nominalAnimationDuration * selectedFps))
  const frameCount = settings.maxFramesEnabled
    ? Math.max(2, Math.min(requestedFrameCount, settings.maxFrames))
    : requestedFrameCount
  const outputFps = settings.maxFramesEnabled && frameCount < requestedFrameCount
    ? frameCount / Math.max(MIN_TRIM_SECONDS, nominalAnimationDuration)
    : selectedFps
  const animationDuration = frameCount / Math.max(1, outputFps)
  const epsilon = Math.min(0.001, trimDuration / 1000)
  const morphAmount = clamp(Number(settings.morphAmount) || 0, 0, 100)
  const frames = []

  for (let index = 0; index < frameCount; index += 1) {
    const outputProgress = index / frameCount
    const sourceProgress = clamp(directionProgress(outputProgress), 0, 1)
    const sourceTime = clamp(
      state.trimStart + sourceProgress * trimDuration,
      state.trimStart,
      Math.max(state.trimStart, state.trimEnd - epsilon),
    )
    frames.push(sourceSpecAtTime(sourceTime, morphAmount, Number(settings.motionBlur) || 0))
  }

  const sourceFrames = getTrimSourceIndices().length
  const forwardSampleFrames = Math.max(1, Math.round(trimDuration * selectedFps / speed))
  const baseSequence = frames.map(dominantSource)
  const interpolatedFrames = frames.reduce((count, frame) => count + (frame.morphFrame ? 1 : 0), 0)

  return {
    trimDuration,
    sourceFrames,
    sourceFps,
    selectedFps,
    baseFps: selectedFps,
    outputFps,
    forwardIndices: baseSequence.slice(0, forwardSampleFrames),
    baseSequence,
    frames,
    totalFrames: frames.length,
    sampledFrames: forwardSampleFrames,
    morphFramesAdded: interpolatedFrames,
    interpolatedFrames,
    morphFramesRequested: morphAmount,
    morphFramesApplied: morphAmount,
    morphWasLimited: false,
    uniqueOutputFrames: new Set(baseSequence).size,
    animationDuration,
    outputDuration: animationDuration,
    delayMs: 1000 / Math.max(1, outputFps),
    wasCapped: frameCount < requestedFrameCount,
    duplicatesAllowed: !settings.smartTiming,
    motionBlur: Number(settings.motionBlur) || 0,
  }
}

export function getPreviewPlan(settings = getSettings()) {
  const output = getFramePlan(settings)
  const frames = sampleEvenly(output.frames, PREVIEW_FRAME_CAP)
  return {
    ...output,
    frames,
    totalFrames: frames.length,
    previewFrameDuration: output.animationDuration / Math.max(1, frames.length),
    previewWasReduced: frames.length < output.frames.length,
  }
}

export function chooseWorkerCount(dimensions = getOutputDimensions(), frameCount = getFramePlan().totalFrames, settings = getSettings()) {
  const hardwareThreads = Math.max(1, Number(navigator.hardwareConcurrency) || 2)
  const deviceMemoryGb = Math.max(1, Number(navigator.deviceMemory) || 4)
  const cpuLimit = Math.max(1, hardwareThreads - 1)
  const platformLimit = deviceMemoryGb <= 2 ? 2 : deviceMemoryGb <= 4 ? 4 : 8
  const pixels = Math.max(1, dimensions.width * dimensions.height)
  const buffersPerJob = settings.morphAmount > 0 || settings.motionBlur > 0 ? 8 : 5
  const estimatedPerWorker = Math.max(16 * 1024 ** 2, pixels * 4 * buffersPerJob)
  const memoryBudget = deviceMemoryGb * 1024 ** 3 * 0.18
  const memoryLimit = Math.max(1, Math.floor(memoryBudget / estimatedPerWorker))
  const formatLimit = settings.format === "webp" && pixels > 1_000_000 ? 4 : 8
  const pixelLoad = Math.max(0.35, pixels / 250_000)
  const morphLoad = 1 + (Number(settings.morphAmount) || 0) / 250
  const blurLoad = 1 + (Number(settings.motionBlur) || 0) / 180
  const formatLoad = settings.format === "webp" ? 1.15 : 1
  const complexityLimit = Math.max(1, Math.floor(cpuLimit / Math.sqrt(pixelLoad * morphLoad * blurLoad * formatLoad)))
  return clamp(Math.min(cpuLimit, platformLimit, memoryLimit, formatLimit, complexityLimit, Math.max(1, frameCount)), 1, 8)
}

export function estimateOutputBytes(dimensions, plan, settings = getSettings()) {
  const pixels = dimensions.width * dimensions.height
  const filterFactor = 1 + Math.abs(settings.contrast - 100) / 500 + settings.ditherStrength / 700
  const blurFactor = 1 + (Number(settings.motionBlur) || 0) / 500
  if (settings.format === "webp") {
    const qualityFactor = 0.045 + settings.webpQuality / 100 * 0.17
    return Math.round(pixels * plan.totalFrames * qualityFactor * filterFactor * blurFactor)
  }
  const colorFactor = 0.012 + settings.colors / 256 * 0.055
  const qualityFactor = 0.58 + settings.gifQuality / 100 * 0.75
  const ditherFactor = settings.ditherMethod === "none" ? 0.9 : 1 + settings.ditherStrength / 240
  return Math.round(pixels * plan.totalFrames * colorFactor * qualityFactor * ditherFactor * blurFactor)
}

export function refreshMetrics(statusOverride = null) {
  const settings = getSettings()
  const plan = getFramePlan(settings)
  const dimensions = getOutputDimensions(settings)
  const workerCount = chooseWorkerCount(dimensions, plan.totalFrames, settings)

  el.fpsValue.textContent = `${settings.fps.toFixed(1)} fps`
  el.fpsHelper.textContent = plan.wasCapped
    ? `Final output reduced to ${plan.outputFps.toFixed(1)} fps by Maximum frames`
    : `Final output: ${plan.outputFps.toFixed(1)} fps at ${formatSpeed(state.speed)}; morphing does not multiply FPS`
  el.resolutionValue.textContent = `${settings.resolution} px`
  el.scaleValue.textContent = `${settings.scale}%`
  el.morphValue.textContent = `${settings.morphAmount}%`
  el.morphHelper.textContent = settings.morphAmount
    ? `${plan.interpolatedFrames} of ${plan.totalFrames} output frames interpolated inside the fixed ${plan.outputFps.toFixed(1)} fps budget`
    : "Off"
  el.maxFramesValue.textContent = String(settings.maxFrames)
  el.maxFramesSlider.disabled = !settings.maxFramesEnabled
  el.maxFramesCard.classList.toggle("disabled-control", !settings.maxFramesEnabled)
  el.gifQualityValue.textContent = `${settings.gifQuality}%`
  el.colorsValue.textContent = String(settings.colors)
  el.ditherValue.textContent = `${settings.ditherStrength}%`
  el.ditherStrengthCard.classList.toggle("disabled-control", settings.ditherMethod === "none")
  el.ditherSlider.disabled = settings.ditherMethod === "none"
  el.webpQualityValue.textContent = `${settings.webpQuality}%`
  el.webpQualitySlider.disabled = settings.webpMaximumQuality
  el.brightnessValue.textContent = `${settings.brightness}%`
  el.saturationValue.textContent = `${settings.saturation}%`
  el.contrastValue.textContent = `${settings.contrast}%`
  el.grayscaleValue.textContent = `${settings.grayscale}%`
  el.workerPill.textContent = `Workers: ${workerCount}`

  const formatName = state.format === "gif" ? "GIF" : "WebP"
  el.statFramesLabel.textContent = state.direction === "rebound"
    ? `${formatName} frames (out + back)`
    : `${formatName} frames`
  el.statLengthLabel.textContent = `${formatName} length`
  el.statFpsLabel.textContent = `${formatName} fps`

  if (!state.file || !state.mediaReady) {
    for (const target of [el.statResolution, el.statOutputFrames, el.statVideoFrames, el.statSampledFrames, el.statMorphFrames, el.statOutputLength, el.statVideoLength, el.statOutputFps, el.statEstimatedSize]) target.textContent = "—"
    if (statusOverride) el.statusText.textContent = statusOverride
    return
  }

  el.statResolution.textContent = `${dimensions.width}×${dimensions.height}`
  el.statOutputFrames.textContent = `${plan.totalFrames}${plan.wasCapped ? " capped" : ""}`
  el.statVideoFrames.textContent = String(plan.sourceFrames)
  el.statSampledFrames.textContent = String(plan.sampledFrames)
  el.statMorphFrames.textContent = String(plan.interpolatedFrames)
  el.statOutputLength.textContent = formatTime(plan.outputDuration)
  el.statVideoLength.textContent = formatTime(plan.trimDuration)
  el.statOutputFps.textContent = plan.outputFps.toFixed(1)
  el.statEstimatedSize.textContent = `~${formatBytes(estimateOutputBytes(dimensions, plan, settings))}`

  if (statusOverride) {
    el.statusText.textContent = statusOverride
  } else if (!state.encoding) {
    const directionText = state.direction === "rebound" ? " · out + back" : ""
    const morphText = plan.interpolatedFrames ? ` · ${plan.interpolatedFrames} interpolated` : ""
    const blurText = settings.motionBlur ? ` · ${settings.motionBlur}% motion blur` : ""
    el.statusText.textContent = `${plan.sourceFrames} source → ${plan.sampledFrames} timeline samples → ${plan.totalFrames} ${formatName} at ${plan.outputFps.toFixed(1)} fps${directionText}${morphText}${blurText}`
  }
}
